import { useState } from 'react';
import { AssignmentDrawer, type DrawerTarget } from '../components/AssignmentDrawer';
import { colorFor } from '../components/chart/palette';
import type { AggregateBundle, AreaId, Filters, Meta } from '../types';

interface AreaViewProps {
  stats: AggregateBundle;
  filters: Filters;
  meta: Meta | undefined;
  onFilterChange: (patch: Partial<Filters>) => void;
  /** Sätter filtret och byter till Uppdrag-fliken. */
  onShowAssignments: (patch: Partial<Filters>) => void;
}

/** Så många tekniker får plats i ett kort utan att kortet tar över vyn. Är
 *  området valt visas alla, för då är det just det man bett om. */
const TECH_PREVIEW = 10;

/**
 * Ett kort per område med de tekniker som faktiskt efterfrågas där. Det är den
 * vy en säljare vill ha framför sig: "vad ska vi kunna för att ta test-uppdrag".
 * Varje teknik öppnar annonserna bakom siffran i en sidopanel.
 */
export function AreaView({ stats, filters, meta, onFilterChange, onShowAssignments }: AreaViewProps) {
  const [drawerTarget, setDrawerTarget] = useState<DrawerTarget | null>(null);

  const areas = stats.areas.filter((area) => area.count > 0);
  const max = Math.max(1, ...areas.map((area) => area.count));

  if (areas.length === 0) {
    return <p className="empty">Inga uppdrag i urvalet.</p>;
  }

  return (
    <>
      <p className="panel__tools" style={{ marginBottom: 14 }}>
        Klicka på en teknik för att se annonserna bakom siffran. Klickar du på områdets namn filtreras vyn till det området och kortet visar alla tekniker som efterfrågas där.
      </p>

      <div className="panel-grid panel-grid--thirds">
        {areas.map((area, index) => {
          const technologies = stats.techByArea[area.id] ?? [];
          const isSelected = filters.area === area.id;
          const color = colorFor(index);

          return (
            <article key={area.id} className={`area-card${isSelected ? ' area-card--selected' : ''}`}>
              <div className="area-card__head">
                <h3 className="area-card__name">
                  <button
                    type="button"
                    className="area-card__filter"
                    aria-pressed={isSelected}
                    onClick={() => onFilterChange({ area: isSelected ? undefined : (area.id as AreaId) })}
                  >
                    {area.name}
                  </button>
                </h3>
                <button
                  type="button"
                  className="area-card__count"
                  title={`Visa alla ${area.count} uppdrag inom ${area.name}`}
                  onClick={() => setDrawerTarget({ areaId: area.id, areaName: area.name })}
                >
                  {area.count}
                </button>
              </div>

              <div className="area-card__share">
                <span style={{ width: `${(area.count / max) * 100}%`, background: color }} />
              </div>

              <p className="panel__tools" style={{ margin: 0 }}>
                {Math.round((area.count / stats.totalAssignments) * 100)} % av urvalet
                {isSelected && technologies.length > 0 ? ` · alla ${technologies.length} tekniker` : ''}
              </p>

              {technologies.length > 0 ? (
                <div className="area-card__tech">
                  {(isSelected ? technologies : technologies.slice(0, TECH_PREVIEW)).map((tech) => (
                    <button
                      key={tech.name}
                      type="button"
                      className="tag tag--tech"
                      title={`Visa de ${tech.count} uppdragen med ${tech.name} inom ${area.name}`}
                      onClick={() => setDrawerTarget({ tech: tech.name, areaId: area.id, areaName: area.name })}
                    >
                      {tech.name} <span className="tag__count">{tech.count}</span>
                    </button>
                  ))}
                  {!isSelected && technologies.length > TECH_PREVIEW ? (
                    <button
                      type="button"
                      className="tag tag--more"
                      onClick={() => onFilterChange({ area: area.id as AreaId })}
                    >
                      Visa alla tekniker
                    </button>
                  ) : null}
                </div>
              ) : (
                <p className="panel__tools" style={{ margin: 0 }}>
                  Inga tekniker utlästa ur annonserna i det här området.
                </p>
              )}
            </article>
          );
        })}
      </div>

      <AssignmentDrawer
        target={drawerTarget}
        filters={filters}
        meta={meta}
        onClose={() => setDrawerTarget(null)}
        onShowAll={onShowAssignments}
      />
    </>
  );
}
